import {
  ActionConfirmationModal,
  AppModal,
  Button,
  Form,
  FormSubmitButton,
  LoadingSpinner,
  TextAreaFormField,
  TextFormField,
  Text,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useUser } from "@/components/UserProvider";
import {
  retrieveDefaultMatterFileRepositoryStructureFolderQueryOptions,
  useCreateDefaultMatterFileRepositoryStructureFolder,
  useDeleteDefaultMatterFileRepositoryStructureFolder,
} from "@/lib/data/react-query/matters/matter-default-file-repository-structure-folder";
import { DefaultMatterFileRepositoryStructureFolderSchema } from "@/lib/data/schemas";
import {
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type DefaultMatterFileRepositoryStructure,
  type DefaultMatterFileRepositoryStructureFolder,
} from "@/lib/types";
import { cn } from "@/lib/utils/cn";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Folder, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState, type KeyboardEvent } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";
import { DefaultFolderStructureContextMenu } from "./DefaultFolderStructureContextMenu";

const FOLDER_CREATE_FORM_ID = "folder-system-create-folder-form";

/** Stack above the Edit Folder Structure modal (z‑100 / z‑101). */
const ADD_FOLDER_MODAL_OVERLAY_CLASS = "z-[108]";
const ADD_FOLDER_MODAL_CONTENT_CLASS = "z-[109]";

const addFolderModalFieldsetClassName =
  "relative grid min-h-0 grid-cols-1 gap-4 sm:grid-cols-2";

type FolderSystemProps = {
  onFolderSelect?: (folderId: string | null) => void;
  activeFileReposityStructure: DefaultMatterFileRepositoryStructure;
};

const createFolderFormSchema =
  DefaultMatterFileRepositoryStructureFolderSchema.pick({
    name: true,
    description: true,
  });

const defaultFormValues = {
  name: "",
  description: "",
};

export function FolderSystem({
  onFolderSelect,
  activeFileReposityStructure,
}: FolderSystemProps) {
  const { hasActionPermission } = useUser();
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [isAddFolderModalOpen, setIsAddFolderModalOpen] = useState(false);
  const [folderToAddTo, setFolderToAddTo] = useState<string | null>(null);
  const [isAddingAtRoot, setIsAddingAtRoot] = useState(false);
  const [folderToDelete, setFolderToDelete] = useState<string | null>(null);

  const createForm = useForm<z.infer<typeof createFolderFormSchema>>({
    resolver: zodResolver(createFolderFormSchema),
    values: defaultFormValues,
    mode: "onSubmit",
  });

  const { data: foldersData, isLoading: isFoldersLoading } = useQuery(
    retrieveDefaultMatterFileRepositoryStructureFolderQueryOptions(
      activeFileReposityStructure.id,
    ),
  );
  const createDefaultMatterFileRepositoryStructureFolder =
    useCreateDefaultMatterFileRepositoryStructureFolder();
  const deleteDefaultMatterFileRepositoryStructureFolder =
    useDeleteDefaultMatterFileRepositoryStructureFolder();

  const folders = useMemo(() => foldersData?.data ?? [], [foldersData]);

  const isCreatingFolder =
    createDefaultMatterFileRepositoryStructureFolder.isPending;
  const isDeletingFolder =
    deleteDefaultMatterFileRepositoryStructureFolder.isPending;
  const canCreateFolders = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterFileRepositoryStructureFolder,
    ACTION_PERMISSION_VALUES.create,
  );
  const canDeleteFolders = hasActionPermission(
    ACTION_PERMISSION_KEYS.defaultMatterFileRepositoryStructureFolder,
    ACTION_PERMISSION_VALUES.delete,
  );

  const addFolderModalTitle = useMemo(() => {
    if (isAddingAtRoot) {
      return "Create new root folder";
    }
    const rawName = folders.find((f) => f.id === folderToAddTo)?.name ?? "...";
    return `Create new subfolder to "${rawName}"`;
  }, [isAddingAtRoot, folderToAddTo, folders]);

  const handleSelectFolder = (folderId: string) => {
    setSelectedFolder(folderId);
    if (onFolderSelect) {
      onFolderSelect(folderId);
    }
  };

  const openAddDialog = (folderId: string | null = null) => {
    if (folderId) {
      setFolderToAddTo(folderId);
      setIsAddingAtRoot(false);
    } else {
      setFolderToAddTo(null);
      setIsAddingAtRoot(true);
    }
    setIsAddFolderModalOpen(true);
  };

  const requestDeleteFolder = (folderId: string) => {
    setFolderToDelete(folderId);
    handleSelectFolder(folderId);
  };

  const handleAddFolder = async (
    data: z.infer<typeof createFolderFormSchema>,
  ) => {
    const newFolder = {
      structureId: activeFileReposityStructure.id,
      idLocation: isAddingAtRoot ? "" : (folderToAddTo ?? ""),
      ...data,
    };

    await createDefaultMatterFileRepositoryStructureFolder.mutateAsync(
      newFolder,
    );
    createForm.reset();
    setIsAddFolderModalOpen(false);
  };

  const confirmDeleteFolder = async () => {
    if (!folderToDelete) return;
    const idToDelete = folderToDelete;
    await deleteDefaultMatterFileRepositoryStructureFolder.mutateAsync({
      id: idToDelete,
      structureId: activeFileReposityStructure.id,
    });

    if (selectedFolder && idToDelete === selectedFolder) {
      setSelectedFolder(null);
      if (onFolderSelect) {
        onFolderSelect(null);
      }
    }
    setFolderToDelete(null);
  };

  const handleKeyDown = (
    e: KeyboardEvent<HTMLDivElement>,
    folderId: string,
    action?: "select" | "add" | "remove",
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();

      if (action === "add") {
        openAddDialog(folderId);
      } else if (action === "remove") {
        setFolderToDelete(folderId);
      } else {
        handleSelectFolder(folderId);
      }
    }
  };

  const renderFolderSystem = () => {
    const rootFolders = folders.filter(
      (folder) => folder.idLocation === "" || folder.idLocation === null,
    );

    return rootFolders.map((folder, index) =>
      renderFolderWithChildren(folder, 0, index),
    );
  };

  const renderFolderWithChildren = (
    folder: DefaultMatterFileRepositoryStructureFolder,
    level: number,
    index: number,
  ) => {
    const isSelected = selectedFolder === folder.id;

    const children = folders.filter((f) => f.idLocation === folder.id);

    return (
      <div key={folder.id}>
        <DefaultFolderStructureContextMenu
          folder={folder}
          canCreate={canCreateFolders}
          canDelete={canDeleteFolders}
          onCreateSubfolder={() => {
            openAddDialog(folder.id);
          }}
          onDelete={() => {
            requestDeleteFolder(folder.id);
          }}
          trigger={
            <div
              className={cn(
                "group hover:bg-muted/50 flex cursor-pointer items-center rounded py-1 pr-1 pl-2 transition-colors",
                isSelected && "bg-primary/10 text-primary",
                "focus-visible:ring-ring focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none",
              )}
              style={{ paddingLeft: `${level * 16 + 8}px` }}
              onClick={(e) => {
                e.stopPropagation();
                handleSelectFolder(folder.id);
              }}
              onKeyDown={(e) => {
                handleKeyDown(e, folder.id);
              }}
              tabIndex={0}
              role="treeitem"
              aria-selected={isSelected}
              aria-expanded={true}
              aria-level={level + 1}
              aria-posinset={index + 1}
              aria-setsize={
                folders.filter((f) => f.idLocation === folder.idLocation).length
              }
            >
              <Folder
                className="mr-2 h-5 w-5 shrink-0 text-amber-500"
                aria-hidden="true"
              />
              <span className="flex-grow truncate">{folder.name}</span>

              <div className="flex items-center gap-1 opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100">
                {canCreateFolders && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-foreground h-6 w-6 focus-visible:ring-1"
                    onClick={(e) => {
                      e.stopPropagation();
                      openAddDialog(folder.id);
                    }}
                    onKeyDown={(e) => {
                      handleKeyDown(
                        // @ts-expect-error Not sure why this is an error
                        e,
                        folder.id,
                        "add",
                      );
                    }}
                    tabIndex={0}
                    aria-label={`Create subfolder to ${folder.name}`}
                    data-testid="create-subfolder-button"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                )}

                {canDeleteFolders && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="text-muted-foreground hover:text-destructive h-6 w-6 focus-visible:ring-1"
                    onClick={() => {
                      requestDeleteFolder(folder.id);
                    }}
                    onKeyDown={(e) => {
                      handleKeyDown(
                        // @ts-expect-error Not sure why this is an error

                        e,
                        folder.id,
                        "remove",
                      );
                    }}
                    tabIndex={0}
                    aria-label={`Delete folder ${folder.name}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          }
        />

        {children.length > 0 && (
          <div role="group">
            {children.map((child, childIndex) =>
              renderFolderWithChildren(child, level + 1, childIndex),
            )}
          </div>
        )}
      </div>
    );
  };

  useEffect(() => {
    if (!isAddFolderModalOpen) {
      createForm.reset();
    }
  }, [isAddFolderModalOpen, createForm]);

  return (
    <>
      <div className="bg-card text-foreground p-2">
        <div className="mb-2 flex items-center justify-between border-b p-2">
          <h3 className="font-medium">Folders</h3>
          {canCreateFolders && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={() => {
                openAddDialog();
              }}
              aria-label="Add root folder"
            >
              {createDefaultMatterFileRepositoryStructureFolder.isPending ? (
                <LoadingSpinner />
              ) : (
                <>
                  <Plus className="h-4 w-4" aria-hidden="true" />
                  <Text as="span" variant="body">
                    Add root folder
                  </Text>
                </>
              )}
            </Button>
          )}
        </div>
        {isFoldersLoading && (
          <div className="text-muted-foreground justify-items-center p-4 text-center">
            <LoadingSpinner />
          </div>
        )}
        {!isFoldersLoading && folders.length === 0 ? (
          <div className="text-muted-foreground p-4 text-center">
            {`No folders yet. Click "Create folder" to create one.`}
          </div>
        ) : (
          <div role="tree" aria-label="Folder structure">
            {renderFolderSystem()}
          </div>
        )}
      </div>
      <Form {...createForm}>
        <AppModal
          open={isAddFolderModalOpen}
          onOpenChange={setIsAddFolderModalOpen}
          title={addFolderModalTitle}
          size="lg"
          loading={isCreatingFolder}
          isDirty={createForm.formState.isDirty}
          hideCloseButton={isCreatingFolder}
          overlayClassName={ADD_FOLDER_MODAL_OVERLAY_CLASS}
          className={ADD_FOLDER_MODAL_CONTENT_CLASS}
          footer={
            <div className="flex w-full justify-end gap-x-2">
              <FormCancelButton
                setOpen={setIsAddFolderModalOpen}
                isDirty={createForm.formState.isDirty}
              />
              <FormSubmitButton
                submitFormId={FOLDER_CREATE_FORM_ID}
                loading={isCreatingFolder}
                title="Save"
                variant="foreground"
              />
            </div>
          }
        >
          <form
            id={FOLDER_CREATE_FORM_ID}
            className="relative flex min-h-0 flex-col"
            onSubmit={async (e) => {
              e.preventDefault();
              e.stopPropagation();
              await createForm.handleSubmit(handleAddFolder)();
            }}
          >
            <fieldset
              disabled={isCreatingFolder}
              className={addFolderModalFieldsetClassName}
            >
              <div className="col-span-1 sm:col-span-2">
                <TextFormField
                  register={createForm.register("name")}
                  label="Folder name*"
                />
              </div>
              <div className="col-span-1 sm:col-span-2">
                <TextAreaFormField
                  register={createForm.register("description")}
                  label="Description"
                />
              </div>
            </fieldset>
          </form>
        </AppModal>
      </Form>
      <ActionConfirmationModal
        title="Are you sure
        you want to delete this folder?"
        description="This action cannot be undone."
        open={!!folderToDelete}
        setOpen={(nextOpen) => {
          if (!nextOpen && !isDeletingFolder) {
            setFolderToDelete(null);
          }
        }}
        isActionPending={isDeletingFolder}
        afterActionFn={confirmDeleteFolder}
      />
    </>
  );
}
